import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

import { solidTone, type ToneName } from './tones';

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

/**
 * Os estados e tambem as categorias (`tones.ts`), para a etiqueta que so separa uma
 * coisa da outra, como o produto e a modalidade no cabecalho da operacao.
 */
export type BadgeColor =
  BadgeTone | Extract<ToneName, 'violet' | 'cyan' | 'indigo' | 'teal' | 'rose'>;

const tones: Record<BadgeColor, string> = {
  neutral: 'bg-ink-100 text-ink-700 ring-ink-200',
  brand: 'bg-brand-50 text-brand-700 ring-brand-200',
  success: 'bg-success-50 text-success-700 ring-success-200',
  warning: 'bg-warning-50 text-warning-700 ring-warning-200',
  danger: 'bg-danger-50 text-danger-700 ring-danger-200',
  info: 'bg-info-50 text-info-700 ring-info-200',
  violet: 'bg-violet-50 text-violet-700 ring-violet-200',
  cyan: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
  indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  teal: 'bg-teal-50 text-teal-700 ring-teal-200',
  rose: 'bg-rose-50 text-rose-700 ring-rose-200',
};

/**
 * Etiqueta curta.
 *
 * O ponto (`dot`) marca a etiqueta de situacao, como a da `StatusBadge`. Ele repete a
 * cor, e o texto continua sendo o que diz a situacao: quem nao distingue as cores le
 * a palavra.
 */
export function Badge({
  tone = 'neutral',
  dot = false,
  children,
  className,
}: {
  tone?: BadgeColor;
  dot?: boolean | undefined;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        // O raio de 0,625rem e o de uma pilula numa linha so; quando a celula estreita
        // quebra o texto, a etiqueta vira um retangulo arredondado, e nao uma capsula.
        'inline-flex items-center rounded-[0.625rem] py-0.5',
        'text-2xs font-semibold ring-1 ring-inset',
        // O ponto ocupa parte do respiro das laterais, para a etiqueta quase nao crescer:
        // na coluna estreita da lista de operacoes, "Aguardando comercial" cabe numa
        // linha com e sem o ponto.
        dot ? 'gap-[3px] px-1.5' : 'gap-1 px-2',
        tones[tone],
        className,
      )}
    >
      {dot ? (
        <span
          aria-hidden
          className={cn('size-[5px] shrink-0 rounded-full', solidTone[tone])}
        />
      ) : null}
      {children}
    </span>
  );
}
